import { useAuth } from 'react-oidc-context';
import { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import axios from 'axios';
import { SET_USER_AUTHENTICATED } from '../common/redux/actionTypes';
import { isDemoMode } from '../config/authConfig';

export function useAppAuth() {
  const dispatch = useDispatch();
  const isDemo = isDemoMode();
  const hasInitialized = useRef(false);

  // Always call useAuth unconditionally to satisfy React's rules of hooks
  const auth = useAuth();

  useEffect(() => {
    // Prevent multiple initializations
    if (hasInitialized.current) return;

    if (isDemo) {
      // Demo mode - set as authenticated without token
      dispatch({
        type: SET_USER_AUTHENTICATED,
        isAuthenticated: true,
        isAdmin: false,
        jwtToken: null,
        demoMode: true
      });
      hasInitialized.current = true;
    } else if (auth && auth.isAuthenticated && auth.user) {
      // Get the access token for API calls (recommended for API authorization)
      const accessToken = auth.user?.access_token;

      // Fetch user context from API to get is_admin status
      const fetchUserContext = async () => {
        try {
          const baseApiUri = process.env.REACT_APP_BASE_URL || 'http://localhost:8080';
          const response = await axios.get(`${baseApiUri}/users/context`, {
            headers: {
              Authorization: accessToken
            }
          });

          const userContext = response.data;

          // Check if this is a new signup (state parameter or newly created account)
          // We can check if user was created very recently (within last 5 minutes)
          const createdAt = new Date(userContext.created_at);
          const now = new Date();
          const fiveMinutesAgo = new Date(now.getTime() - 5 * 60 * 1000);
          const isNewSignup = createdAt > fiveMinutesAgo;

          // Also check URL params for state=new_signup from OAuth callback
          const urlParams = new URLSearchParams(window.location.search);
          const hasSignupState = urlParams.get('state') === 'new_signup';

          // Dispatch with is_admin from API
          dispatch({
            type: SET_USER_AUTHENTICATED,
            isAuthenticated: true,
            isAdmin: userContext.is_admin || false,
            jwtToken: accessToken,
            userContext: userContext, // Store full context if needed
            isNewSignup: isNewSignup || hasSignupState,
            demoMode: false,
            companyRoles: userContext.company_roles || {}
          });
          hasInitialized.current = true;
        } catch (error) {
          console.error('Failed to fetch user context:', error);

          // Fallback: authenticate without admin status if API call fails
          dispatch({
            type: SET_USER_AUTHENTICATED,
            isAuthenticated: true,
            isAdmin: false, // Default to false if we can't fetch context
            jwtToken: accessToken,
            demoMode: false,
            companyRoles: {},
            permissionsError: true
          });
          hasInitialized.current = true;
        }
      };

      fetchUserContext();
    } else if (auth && !auth.isLoading && !auth.isAuthenticated && !isDemo) {
      // Not authenticated and not in demo mode - trigger login
      // Store the current path to redirect back after login
      const currentPath = window.location.pathname + window.location.search;
      if (currentPath !== '/' && currentPath !== '/callback') {
        sessionStorage.setItem('returnUrl', currentPath);
      }

      // Redirect to Cognito Hosted UI
      auth.signinRedirect().catch(error => {
        console.error('Failed to redirect to login:', error);
      });
    }
  }, [auth?.isAuthenticated, auth?.isLoading, auth?.user, isDemo, dispatch]);

  // Helper function to refresh token manually if needed
  const refreshToken = async () => {
    if (!isDemo && auth && auth.user) {
      try {
        await auth.signinSilent();
        return true;
      } catch (error) {
        console.error('Token refresh failed:', error);
        return false;
      }
    }
    return false;
  };

  // Get current JWT token (access token for API authorization)
  const getToken = () => {
    if (isDemo) return null;
    return auth?.user?.access_token || null;
  };

  return {
    isLoading: !isDemo && auth?.isLoading,
    isAuthenticated: isDemo || (auth?.isAuthenticated || false),
    isAdmin: false, // This will be set from Redux state via the API call
    isDemoMode: isDemo,
    user: auth?.user,
    signOut: async () => {
      if (isDemo) {
        // For demo mode, just redirect to home page
        window.location.href = '/';
      } else if (auth && auth.isAuthenticated) {
        // Clear local storage and session storage
        sessionStorage.clear();
        localStorage.clear();

        // For Cognito hosted UI, we need to construct the logout URL properly
        // The logout endpoint expects 'client_id' and 'logout_uri' as query parameters
        const logoutUrl = new URL(`https://${process.env.REACT_APP_COGNITO_OAUTH_DOMAIN}/logout`);
        logoutUrl.searchParams.append('client_id', process.env.REACT_APP_COGNITO_USER_POOL_CLIENT_ID);
        logoutUrl.searchParams.append('logout_uri', process.env.REACT_APP_COGNITO_POST_LOGOUT_URI);

        // Redirect directly to Cognito logout URL
        window.location.href = logoutUrl.toString();
      }
    },
    refreshToken,
    getToken
  };
}